// ─────────────────────────────────────────────────────────────────────────────
// fsa_journal_client.ts — IJournalClient implementation backed by a
// FileSystemDirectoryHandle (the "vault folder" the user picks).
//
// Mirrors the on-disk layout of run_pwa.py / journal.py so the same folder
// can be opened by either runtime and produce the same UX:
//
//   <root>/                              user-picked vault folder
//     .ox/
//       journal.json                     manifest (title, createdAt, …)
//       session.json                     last currentPageId / currentFolderPath
//       folder.json                      root landing-page metadata
//       ink/<rel-path>.json              stroke sidecars for non-native drivers
//     <subfolder>/
//       .ox.folder.json                  per-folder landing-page metadata
//       <name>.ox.json                   native-json page (lossless)
//       <name>.md                        text page (lossy; strokes go in sidecar)
//
// Depth-bounded recursive directory walks build the same TreeNode shape the
// HTTP server returns, so standalone_host can drive both via the same
// `_initFromJournal` lifecycle without branching on the backend.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  FolderInfo,
  IJournalClient,
  JournalDriverInfo,
  JournalInfo,
  PageReadResponse,
  PageSaveRequest,
  PageSaveResponse,
  TreeNode,
} from './journal_client';

// ── Constants (mirror journal.py defaults) ──────────────────────────────────

const MANIFEST_VERSION = 1;

const DEFAULT_LEVEL_LABELS: Record<string, string> = {
  '0': 'root',
  '1': 'collection',
  '2': 'chapter',
  '3': 'section',
  '4': 'subsection',
};

const RESERVED_DIRS: string[] = [
  'assets', 'figures', 'images', 'resources', 'archive', 'trash', 'temporal',
  'node_modules', '__pycache__', '.venv', 'venv', 'out', 'dist', 'build',
];
const RESERVED_DIRS_SET = new Set(RESERVED_DIRS.map(d => d.toLowerCase()));

const NATIVE_DRIVER: JournalDriverInfo = {
  name: 'native-json', extensions: ['.ox.json'], kind: 'native', lossy: false,
};
const TEXT_DRIVER: JournalDriverInfo = {
  name: 'text', extensions: ['.md', '.markdown', '.txt'], kind: 'text', lossy: true,
};
const DRIVERS: JournalDriverInfo[] = [NATIVE_DRIVER, TEXT_DRIVER];

const TEXT_EXTS = new Set(TEXT_DRIVER.extensions);

const DOT_OX = '.ox';
const FILE_MANIFEST = 'journal.json';
const FILE_SESSION = 'session.json';
const FILE_ROOT_FOLDER_INFO = 'folder.json';     // inside .ox/
const FILE_SUBFOLDER_INFO = '.ox.folder.json';   // inside the subfolder itself
const DIR_INK = 'ink';                            // .ox/ink/<rel-path>.json
const PAGE_EXT = '.ox.json';

// ── Internal types ─────────────────────────────────────────────────────────

interface Manifest {
  version?: number;
  title?: string;
  createdAt?: number;
  levelLabels?: Record<string, string>;
  reservedDirs?: string[];
}

// ── Driver pick (mirrors longest-extension-wins rule) ──────────────────────

function pickDriver(filename: string): JournalDriverInfo | null {
  const lower = filename.toLowerCase();
  if (lower.endsWith(PAGE_EXT)) return NATIVE_DRIVER;
  for (const ext of TEXT_DRIVER.extensions) {
    if (lower.endsWith(ext)) return TEXT_DRIVER;
  }
  return null;
}

function stemForLeaf(filename: string, driver: JournalDriverInfo): string {
  const lower = filename.toLowerCase();
  // Pick the longest matching extension
  let bestExt = '';
  for (const ext of driver.extensions) {
    if (lower.endsWith(ext) && ext.length > bestExt.length) bestExt = ext;
  }
  return bestExt ? filename.slice(0, filename.length - bestExt.length) : filename;
}

// ── Handle iteration helpers (TS lib types miss .entries() / .values()) ────

interface DirIterable {
  entries(): AsyncIterable<[string, FileSystemHandle]>;
}
function asIterable(handle: FileSystemDirectoryHandle): DirIterable {
  return handle as unknown as DirIterable;
}

async function listEntries(
  dir: FileSystemDirectoryHandle,
): Promise<Array<{ name: string; handle: FileSystemHandle }>> {
  const out: Array<{ name: string; handle: FileSystemHandle }> = [];
  for await (const [name, h] of asIterable(dir).entries()) {
    out.push({ name, handle: h });
  }
  return out;
}

// ── Path / handle resolution ───────────────────────────────────────────────

function splitPath(rel: string): string[] {
  return rel.split('/').filter(Boolean);
}

function joinPath(parts: string[]): string {
  return parts.join('/');
}

async function resolveDir(
  root: FileSystemDirectoryHandle,
  rel: string,
  create = false,
): Promise<FileSystemDirectoryHandle | null> {
  let cur: FileSystemDirectoryHandle = root;
  for (const part of splitPath(rel)) {
    try {
      cur = await cur.getDirectoryHandle(part, { create });
    } catch (e) {
      if ((e as DOMException)?.name === 'NotFoundError') return null;
      throw e;
    }
  }
  return cur;
}

async function resolveFile(
  root: FileSystemDirectoryHandle,
  rel: string,
  create = false,
): Promise<FileSystemFileHandle | null> {
  const parts = splitPath(rel);
  if (parts.length === 0) return null;
  const filename = parts[parts.length - 1];
  const dirRel = joinPath(parts.slice(0, -1));
  const dir = await resolveDir(root, dirRel, create);
  if (!dir) return null;
  try {
    return await dir.getFileHandle(filename, { create });
  } catch (e) {
    if ((e as DOMException)?.name === 'NotFoundError') return null;
    throw e;
  }
}

// ── Low-level read/write ───────────────────────────────────────────────────

async function readJsonFile<T>(file: FileSystemFileHandle): Promise<T | null> {
  const f = await file.getFile();
  if (f.size === 0) return null;
  const text = await f.text();
  try { return JSON.parse(text) as T; } catch { return null; }
}

async function writeJsonFile(file: FileSystemFileHandle, value: unknown): Promise<number> {
  const text = JSON.stringify(value, null, 2);
  const w = await file.createWritable();
  await w.write(text);
  await w.close();
  return text.length;
}

async function readTextFile(file: FileSystemFileHandle): Promise<string> {
  const f = await file.getFile();
  if (f.size === 0) return '';
  // Try UTF-8 first; if invalid sequences appear (replacement chars),
  // fall back to latin-1 so a weirdly-encoded file still renders
  // legibly rather than as a wall of U+FFFD. Mirrors the Python
  // TextDriver's encoding fallback in journal.py.
  const buf = await f.arrayBuffer();
  const utf8 = new TextDecoder('utf-8', { fatal: false }).decode(buf);
  if (utf8.indexOf('�') === -1) return utf8;
  return new TextDecoder('latin1').decode(buf);
}

async function writeTextFile(file: FileSystemFileHandle, text: string): Promise<number> {
  const w = await file.createWritable();
  await w.write(text);
  await w.close();
  return text.length;
}

async function tryReadJsonAt<T>(
  root: FileSystemDirectoryHandle, rel: string,
): Promise<T | null> {
  const fh = await resolveFile(root, rel, false);
  if (!fh) return null;
  return readJsonFile<T>(fh);
}

async function writeJsonAt(
  root: FileSystemDirectoryHandle, rel: string, value: unknown,
): Promise<number> {
  const fh = await resolveFile(root, rel, true);
  if (!fh) throw new Error(`failed to open ${rel} for write`);
  return writeJsonFile(fh, value);
}

/** Best-effort file remove. Walks the relative path, resolves the
 *  parent directory, and calls removeEntry on the file. Missing
 *  intermediate dirs / missing file = no-op (the caller treats
 *  this as best-effort). Browsers that don't support
 *  FileSystemDirectoryHandle.removeEntry simply throw and we
 *  silently swallow. */
async function removeFileAt(
  root: FileSystemDirectoryHandle, rel: string,
): Promise<void> {
  const segs = rel.split('/').filter(Boolean);
  if (segs.length === 0) return;
  const filename = segs[segs.length - 1];
  let dir = root;
  try {
    for (let i = 0; i < segs.length - 1; i++) {
      dir = await dir.getDirectoryHandle(segs[i], { create: false });
    }
    await (dir as unknown as { removeEntry: (name: string) => Promise<void> }).removeEntry(filename);
  } catch {
    // Missing directory / missing file / no removeEntry support —
    // best-effort, swallow.
  }
}

// ── Tree walk ──────────────────────────────────────────────────────────────

function isHidden(name: string): boolean {
  return name.startsWith('.');
}

function isReservedDir(name: string): boolean {
  return RESERVED_DIRS_SET.has(name.toLowerCase());
}

function compareEntries(
  a: { name: string; handle: FileSystemHandle },
  b: { name: string; handle: FileSystemHandle },
): number {
  const aDir = a.handle.kind === 'directory';
  const bDir = b.handle.kind === 'directory';
  if (aDir !== bDir) return aDir ? -1 : 1;          // folders first
  return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
}

interface WalkOpts {
  depth: number;       // remaining depth; 0 means "this node only, no recursion"
  level: number;       // depth from journal root (0 = root)
  basePath: string;    // forward-slash relative path of the current dir
  levelLabels: Record<string, string>;
}

async function walkDir(
  dir: FileSystemDirectoryHandle,
  opts: WalkOpts,
  rootTitle: string,
): Promise<TreeNode> {
  const { depth, level, basePath, levelLabels } = opts;
  const entries = await listEntries(dir);
  entries.sort(compareEntries);

  const children: TreeNode[] = [];
  for (const { name, handle } of entries) {
    if (isHidden(name)) continue;
    if (handle.kind === 'directory') {
      if (isReservedDir(name)) continue;
      const childPath = basePath === '' ? name : `${basePath}/${name}`;
      const childLevel = level + 1;
      const labelKey = String(childLevel);
      const childLabel = levelLabels[labelKey] ?? `level${childLevel}`;
      if (depth <= 0) {
        // Container, but children not expanded yet (lazy).
        children.push({
          id: childPath,
          title: name,
          level: childLevel,
          levelLabel: childLabel,
          isContainer: true,
          driver: null,
        });
      } else {
        const sub = await walkDir(handle as FileSystemDirectoryHandle, {
          depth: depth - 1,
          level: childLevel,
          basePath: childPath,
          levelLabels,
        }, rootTitle);
        children.push(sub);
      }
    } else {
      const driver = pickDriver(name);
      if (!driver) continue;
      const childPath = basePath === '' ? name : `${basePath}/${name}`;
      const childLevel = level + 1;
      const labelKey = String(childLevel);
      const childLabel = levelLabels[labelKey] ?? `level${childLevel}`;
      children.push({
        id: childPath,
        title: stemForLeaf(name, driver),
        level: childLevel,
        levelLabel: childLabel,
        isContainer: false,
        driver: driver.name,
      });
    }
  }

  return {
    id: basePath,
    title: basePath === '' ? rootTitle : (basePath.split('/').pop() ?? basePath),
    level,
    levelLabel: levelLabels[String(level)] ?? `level${level}`,
    isContainer: true,
    driver: null,
    children,
  };
}

// ── Sidecar paths (for non-native page strokes) ────────────────────────────

function sidecarPathFor(rel: string): string {
  // .ox/ink/<rel>.json — mirrors the relative folder structure. E.g.
  // notes/todo.md → .ox/ink/notes/todo.md.json
  return `${DOT_OX}/${DIR_INK}/${rel}.json`;
}

// ── Native-json read/write (lossless) ──────────────────────────────────────

interface NativePageFile {
  $schema?: string;
  id?: string;
  title?: string;
  sectionId?: string;
  color?: string;
  isSectionHeader?: boolean;
  bookmark?: unknown;
  densityLevel?: number;
  defaultLayer?: number;
  createdAt?: number;
  updatedAt?: number;
  blocks?: unknown[];
  arrows?: unknown[];
  strokes?: unknown[];
}

async function readNative(
  fh: FileSystemFileHandle, id: string,
): Promise<PageReadResponse> {
  const data = (await readJsonFile<NativePageFile>(fh)) ?? {};
  const meta: Record<string, unknown> = {};
  for (const k of [
    'id', 'title', 'sectionId', 'color', 'isSectionHeader', 'bookmark',
    'densityLevel', 'defaultLayer', 'createdAt', 'updatedAt',
  ] as const) {
    const v = (data as Record<string, unknown>)[k];
    if (v !== undefined) meta[k] = v;
  }
  return {
    id,
    driver: NATIVE_DRIVER.name,
    lossy: false,
    blocks: Array.isArray(data.blocks) ? data.blocks : [],
    strokes: Array.isArray(data.strokes) ? data.strokes : [],
    arrows: Array.isArray(data.arrows) ? data.arrows : [],
    meta,
  };
}

async function writeNative(
  root: FileSystemDirectoryHandle, req: PageSaveRequest,
): Promise<PageSaveResponse> {
  const fh = await resolveFile(root, req.id, true);
  if (!fh) throw new Error(`failed to open ${req.id} for write`);
  const meta = (req.meta ?? {}) as NativePageFile;
  const value: NativePageFile = {
    $schema: 'ox.page/1',
    ...meta,
    blocks: req.blocks ?? [],
    arrows: req.arrows ?? [],
    strokes: req.strokes ?? [],
  };
  if (!value.updatedAt) value.updatedAt = Date.now();
  if (!value.createdAt) value.createdAt = value.updatedAt;
  const bytes = await writeJsonFile(fh, value);
  return {
    ok: true, id: req.id, driver: NATIVE_DRIVER.name, bytes,
    sidecars: [], created: true,
  };
}

// ── Text driver (lossy: primary file holds text, sidecar holds strokes) ────

async function readText(
  root: FileSystemDirectoryHandle, fh: FileSystemFileHandle, id: string,
): Promise<PageReadResponse> {
  const text = await readTextFile(fh);
  const sidecar = await tryReadJsonAt<{
    blocks?: unknown[]; strokes?: unknown[]; arrows?: unknown[]; meta?: Record<string, unknown>;
  }>(root, sidecarPathFor(id));

  // Primary block — the full file content as one text block. Shape
  // matches WvTextBlock so the canvas can render it directly:
  //   - `bbox: {x, y, width, height}` (not the lowercase short variant)
  //   - `content` (not `text`)
  //   - `rotation: 0`
  //   - `fontSize`
  //   - `displayMode: 'ocr'` so the renderer treats the text as
  //     content rather than as a placeholder for handwriting.
  //   - `_role: 'primary'` — routes block_renderer to the markdown
  //     path so headings, lists, checkboxes and inline styling render
  //     properly. The previous pagination scheme inside that path was
  //     buggy and broke the render frame; the simplified renderer
  //     ships markdown without subpage splitting.
  const stem = (id.split('/').pop() ?? id).replace(/\.[^.]+$/, '');
  const primary: Record<string, unknown> = {
    id: `${stem}.text.1`,
    kind: 'text',
    content: text,
    fontSize: 14,
    bbox: { x: 40, y: 40, width: 590, height: 740 },
    rotation: 0,
    displayMode: 'ocr',
    _role: 'primary',
  };

  // Sidecar contributes any non-primary blocks the user added on top
  // of the .md (figures, ink overlays). Strokes and arrows likewise.
  const extraBlocks = Array.isArray(sidecar?.blocks) ? sidecar!.blocks! : [];
  const meta: Record<string, unknown> = {
    id,
    title: stemForLeaf(id.split('/').pop() ?? id, TEXT_DRIVER),
    format: 'text',
    extension: id.match(/\.[^.]+$/)?.[0] ?? '',
    ...(sidecar?.meta ?? {}),
  };

  return {
    id,
    driver: TEXT_DRIVER.name,
    lossy: true,
    blocks: [primary, ...extraBlocks],
    strokes: Array.isArray(sidecar?.strokes) ? sidecar!.strokes! : [],
    arrows: Array.isArray(sidecar?.arrows) ? sidecar!.arrows! : [],
    meta,
  };
}

async function writeText(
  root: FileSystemDirectoryHandle, req: PageSaveRequest,
): Promise<PageSaveResponse> {
  const fh = await resolveFile(root, req.id, true);
  if (!fh) throw new Error(`failed to open ${req.id} for write`);

  // Split primary text body from everything else. The primary block is
  // the one tagged `_role: 'primary'` (set by readText) — falls back to
  // the first text-kind block if no role is set, matching journal.py's
  // TextDriver.write logic. Non-primary blocks ride along in the
  // sidecar so the .md/.txt source file stays readable in any external
  // editor.
  const primaryParts: string[] = [];
  const extraBlocks: unknown[] = [];
  let primaryConsumed = false;
  for (const b of req.blocks ?? []) {
    const ob = b as Record<string, unknown>;
    const role = ob._role;
    const kind = ob.kind;
    if (!primaryConsumed && kind === 'text' && (role === 'primary' || role === undefined)) {
      const content = typeof ob.content === 'string'
        ? (ob.content as string)
        : (typeof ob.text === 'string' ? (ob.text as string) : '');
      primaryParts.push(content);
      primaryConsumed = true;
    } else {
      extraBlocks.push(b);
    }
  }
  const bytes = await writeTextFile(fh, primaryParts.join('\n\n'));

  const arrows = req.arrows ?? [];
  const strokes = req.strokes ?? [];
  const sidecars: string[] = [];
  if (extraBlocks.length || strokes.length || arrows.length) {
    const path = sidecarPathFor(req.id);
    await writeJsonAt(root, path, {
      blocks: extraBlocks,
      strokes,
      arrows,
      meta: req.meta ?? {},
      updatedAt: Date.now(),
    });
    sidecars.push(path);
  }
  return {
    ok: true, id: req.id, driver: TEXT_DRIVER.name, bytes, sidecars, created: true,
  };
}

// ── The client ─────────────────────────────────────────────────────────────

export class FsaJournalClient implements IJournalClient {
  private _info: JournalInfo | null = null;

  constructor(private readonly root: FileSystemDirectoryHandle) {}

  async probe(): Promise<JournalInfo | null> {
    try {
      return await this._loadOrCreateManifest();
    } catch (err) {
      console.error('[fsa-journal] probe failed:', err);
      return null;
    }
  }

  async info(): Promise<JournalInfo> {
    return this._info ?? this._loadOrCreateManifest();
  }

  async tree(opts: { path?: string; depth?: number } = {}): Promise<TreeNode> {
    const path = opts.path ?? '';
    const depth = Math.max(0, Math.min(opts.depth ?? 16, 32));
    const info = await this.info();
    const dir = path === '' ? this.root : await resolveDir(this.root, path);
    if (!dir) {
      // Mirror server behaviour: empty container if path missing.
      return {
        id: path, title: path.split('/').pop() ?? path, level: splitPath(path).length,
        levelLabel: info.levelLabels[String(splitPath(path).length)] ?? 'level',
        isContainer: true, driver: null, children: [],
      };
    }
    return walkDir(dir, {
      depth,
      level: path === '' ? 0 : splitPath(path).length,
      basePath: path,
      levelLabels: info.levelLabels,
    }, info.title);
  }

  async readPage(id: string): Promise<PageReadResponse> {
    const fh = await resolveFile(this.root, id, false);
    if (!fh) throw new Error(`page not found: ${id}`);
    const lower = id.toLowerCase();
    if (lower.endsWith(PAGE_EXT)) return readNative(fh, id);
    return readText(this.root, fh, id);
  }

  async savePage(req: PageSaveRequest): Promise<PageSaveResponse> {
    const lower = req.id.toLowerCase();
    if (lower.endsWith(PAGE_EXT)) return writeNative(this.root, req);
    return writeText(this.root, req);
  }

  async deletePage(id: string): Promise<void> {
    // Drop the page file at its relative path, then drop the ink
    // sidecar at .ox/ink/<rel>.json (only meaningful for the text
    // driver, but cheap to try unconditionally). Missing files are
    // ignored — the caller treats deletePage as best-effort.
    await removeFileAt(this.root, id);
    await removeFileAt(this.root, sidecarPathFor(id));
  }

  async getFolderInfo(path: string = ''): Promise<FolderInfo> {
    if (path === '') {
      const data = await tryReadJsonAt<FolderInfo>(this.root, `${DOT_OX}/${FILE_ROOT_FOLDER_INFO}`);
      const info = await this.info();
      return {
        path: '',
        title: data?.title || info.title,
        description: data?.description || '',
      };
    }
    const data = await tryReadJsonAt<FolderInfo>(this.root, `${path}/${FILE_SUBFOLDER_INFO}`);
    return {
      path,
      title: data?.title || (path.split('/').pop() ?? path),
      description: data?.description || '',
    };
  }

  async setFolderInfo(
    info: { path?: string; title?: string; description?: string },
  ): Promise<FolderInfo> {
    const path = info.path ?? '';
    const next: FolderInfo = {
      path,
      title: info.title ?? '',
      description: info.description ?? '',
    };
    const rel = path === ''
      ? `${DOT_OX}/${FILE_ROOT_FOLDER_INFO}`
      : `${path}/${FILE_SUBFOLDER_INFO}`;
    await writeJsonAt(this.root, rel, next);
    return next;
  }

  async loadSession(): Promise<Record<string, unknown>> {
    const data = await tryReadJsonAt<Record<string, unknown>>(
      this.root, `${DOT_OX}/${FILE_SESSION}`,
    );
    return data ?? {};
  }

  async saveSession(session: Record<string, unknown>): Promise<void> {
    await writeJsonAt(this.root, `${DOT_OX}/${FILE_SESSION}`, session);
  }

  // ── Internals ────────────────────────────────────────────────────────────

  private async _loadOrCreateManifest(): Promise<JournalInfo> {
    const dotOx = await this.root.getDirectoryHandle(DOT_OX, { create: true });
    let manifest = await this._readManifest(dotOx);
    let createdFresh = false;
    if (!manifest) {
      manifest = {
        version: MANIFEST_VERSION,
        title: this.root.name || 'Vault',
        createdAt: Date.now(),
        levelLabels: DEFAULT_LEVEL_LABELS,
        reservedDirs: RESERVED_DIRS,
      };
      const fh = await dotOx.getFileHandle(FILE_MANIFEST, { create: true });
      await writeJsonFile(fh, manifest);
      createdFresh = true;
    }
    const info: JournalInfo = {
      root: this.root.name,
      dotOx: DOT_OX,
      title: manifest.title || this.root.name || 'Vault',
      createdAt: manifest.createdAt ?? Date.now(),
      createdFresh,
      manifestVersion: manifest.version ?? MANIFEST_VERSION,
      levelLabels: manifest.levelLabels ?? DEFAULT_LEVEL_LABELS,
      reservedDirs: manifest.reservedDirs ?? RESERVED_DIRS,
      drivers: DRIVERS,
    };
    this._info = info;
    return info;
  }

  private async _readManifest(
    dotOx: FileSystemDirectoryHandle,
  ): Promise<Manifest | null> {
    try {
      const fh = await dotOx.getFileHandle(FILE_MANIFEST);
      return readJsonFile<Manifest>(fh);
    } catch (e) {
      if ((e as DOMException)?.name === 'NotFoundError') return null;
      throw e;
    }
  }
}
